<template>
  <div :class="rootClass">
    <div class="autocomplete-wrapper">
      <PickerInput
        ref="suggestInput"
        :picker-id="pickerId"
        :aria-describedby="pickerId + '-onet-hint ' + pickerId + '-onet-details'"
        :aria-activedescendant="descendantAsign(highlightedIndex, pickerId, '-option-')"
        :aria-controls="showSuggestions ? pickerId + '-suggestions' : null"
        aria-label="ONET search"
        type="text"
      />

      <SuggestionsDropdown
        :picker-id="pickerId"
        :show-suggestions="showSuggestions"
        :suggestions="suggestions"
        :highlighted-index="highlightedIndex"
        :dropdown-style="dropdownStyle"
        dropdowntype="onet"
        :showcode="showCode"
        @select-suggestion="selectSuggestion"
        @update-highlighted="highlightedIndex = $event"
      />

      <LoadingIndicator :loading="loading" />
    </div>

    <div
      v-if="variant === 'job-title'"
      v-show="inputShown"
      :id="pickerId + '-onet-hint'"
      class="hint"
    >
      e.g., job title, company, occupation keyword, or
      <a
        href="https://www.onetonline.org"
        title="Opens in a new window"
        data-tooltip="true"
        target="_blank"
      >
        O*NET Code
        <span
          class="icon-general_link_external"
          aria-hidden="true"
        ></span>
      </a>
      for the occupation.
    </div>

    <div
      v-else
      v-show="inputShown && !extraLinkUrl"
      :id="pickerId + '-onet-hint'"
      class="hint"
    >
      Enter a keyword or O*NET code for the occupation.
      <a
        href="https://www.onetonline.org"
        title="Opens in a new window"
        data-tooltip="true"
        target="_blank"
      >
        Search for O*NET Codes
        <span
          class="icon-general_link_external"
          aria-hidden="true"
        ></span>
      </a>
    </div>

    <div
      v-if="showCode && (shownLabel || shownDescription)"
      :id="pickerId + '-onet-details'"
      class="vue-simple-picker__description"
    >
      <span
        v-if="shownLabel"
        class="vue-simple-picker__short-label"
      >
        {{ chosen?.shortLabel || chosen?.label }}
      </span>
      <span
        v-if="shownDescription"
        class="vue-simple-picker__code"
      >
        {{ chosen?.code }}
      </span>
      <span
        v-if="shownDescription"
        class="vue-simple-picker__label-description"
      >
        {{ chosen?.description }}
      </span>
    </div>

    <input
      :id="inputId"
      :value="fieldValue"
      :name="inputName"
      type="hidden"
    />

    <p v-if="debug">
      <strong>Hidden Input:</strong> {{ inputName }}={{ fieldValue }}<br />
      <strong>Initial Value:</strong> {{ inputValue }}
    </p>
  </div>
</template>

<script>
import BasePicker from './onet_naics_shared';
import LoadingIndicator from './LoadingIndicator.vue';
import PickerInput from './PickerInput.vue';
import SuggestionsDropdown from './SuggestionsDropdown.vue';
import './vue_picker.css';

export default {
  components: {
    LoadingIndicator,
    PickerInput,
    SuggestionsDropdown,
  },

  extends: BasePicker,

  props: {
    extraLinkUrl: { type: String, default: '' },
    variant: {
      type: String,
      default: 'onet',
      validator(val) {
        return ['onet', 'job-title'].includes(val);
      },
    },
  },

  computed: {
    rootClass() {
      return this.variant === 'job-title' ? 'job-title-picker' : 'onet-picker';
    },
    showCode() {
      return this.variant === 'onet';
    },
  },

  methods: {
    apiUrl() {
      return '/lookup/occupation_lookups.json';
    },

    onInputBlur() {
      this.blurTimeout = setTimeout(() => {
        this.showSuggestions = false;
        this.focused = false;

        if (this.chosen != null) return;

        if (this.showCode) {
          this.findBestMatch();
        }

        this.$emit('blur');
      }, 200);
    },

    onSuggestSelect(result) {
      if (result == null) return;

      if (this.variant === 'job-title' && result.display) {
        const match = result.display.match(/^\d{2}-\d{4}\.\d{2}\s*-\s*(.+)$/);
        this.text = match ? match[1] : result.display;
      } else {
        this.text = result[this.textMapping];
      }

      this.justSelected = true;
      this.chosen = result;

      this.$nextTick(() => {
        if (this.$refs.suggestInput) {
          this.$refs.suggestInput.focus();
        }
      });
    },

    async fetchSuggestions() {
      try {
        const results = await this.populateLookupData();

        this.suggestions = results.slice(0, 25); // Max 25
        this.showSuggestions = true;
        this.highlightedIndex = -1;
        this.takeLocation();
      } catch (_error) {
        this.suggestions = [];
        this.showSuggestions = false;
      }
    },

    processAcquiredLookupData(data) {
      this.matches = data;
      this.loading -= 1;
      if (this.focused) { return data; }

      return [];
    },

    setupFromDefaultInput() {
      const {
        inputValue,
        valueMode,
      } = this;

      if (inputValue === '') return;

      let bodyData = null;

      if (this.variant === 'job-title') {
        const forId = valueMode === 'id' && !Number.isNaN(parseInt(inputValue, 10));

        if (forId) {
          bodyData = { id: inputValue };
        } else {
          this.origText = inputValue;
          this.text = inputValue;
        }

        this.acquireLookupData(bodyData).then(() => {});
      } else {
        const forId = valueMode === 'id';

        if (forId) {
          bodyData = { id: inputValue.trim() };
        } else {
          this.origText = inputValue.trim();
          this.text = inputValue.trim();
        }

        this.acquireLookupData(bodyData).then(() => {
          if (forId) {
            this.findFirstMatch();
          } else {
            this.findBestMatch();
          }
        });
      }
    },
  },
};
</script>
